import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import {
  createInstallment, updateInstallment, destroyInstallment,
  generateDefaultInstallments, recordPayment, sendReminder, matchBankTransaction,
} from "../api/installments"
import LettrageModal from "./LettrageModal"
import { fmt, dateFmt } from "../helpers/format"

const STATUS_BADGE = {
  scheduled: "bg-slate-100  text-slate-700  ring-slate-200",
  partial:   "bg-amber-50   text-amber-700  ring-amber-200",
  paid:      "bg-emerald-50 text-emerald-700 ring-emerald-200",
  overdue:   "bg-rose-50    text-rose-700   ring-rose-200",
}

const StatTile = ({ label, value, tone }) => (
  <div className={`px-4 py-3 border rounded-xl ${tone || "bg-slate-50 border-slate-200"}`}>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className="text-[18px] font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
)

const eurosToCents = (input) => {
  if (input == null || input === "") return 0
  const normalized = String(input).replace(/\s+/g, "").replace(",", ".")
  const value = parseFloat(normalized)
  if (isNaN(value)) return 0
  return Math.round(value * 100)
}

const centsToEuros = (cents) => (cents == null ? "" : (cents / 100).toString().replace(".", ","))

const todayISO = () => new Date().toISOString().slice(0, 10)

const PAYMENT_METHODS = ["bank_transfer", "check", "cash", "credit_card", "direct_debit", "lcr", "accepted_bill"]

const InstallmentRow = ({ inst, currency, canManage, hasBankCandidates, onChange, onPay, onRemind, onLettrage, onDelete }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState({
    due_date:     inst.due_date,
    amount_cents: inst.amount_cents,
    label:        inst.label || "",
  })

  const submitEdit = () => {
    onChange(inst, draft).then(() => setEditing(false))
  }

  const remaining   = inst.remaining_cents
  const statusKey   = inst.status
  const overdueDays = inst.overdue_days

  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 text-[12px] text-slate-500 tabular-nums w-12">{inst.position}</td>
      <td className="px-3 py-2 text-[13px] text-slate-900">
        {editing ? (
          <input
            type      = "text"
            value     = {draft.label}
            onChange  = {e => setDraft({ ...draft, label: e.target.value })}
            placeholder = {I18n.t("projects.payments.installment_label_placeholder")}
            className = "w-full h-7 px-2 border border-slate-300 rounded text-[12.5px]"
          />
        ) : (
          inst.label || I18n.t("projects.payments.installment_default_label", { position: inst.position })
        )}
      </td>
      <td className="px-3 py-2 text-[12.5px] text-slate-700 tabular-nums">
        {editing ? (
          <input
            type      = "date"
            value     = {draft.due_date}
            onChange  = {e => setDraft({ ...draft, due_date: e.target.value })}
            className = "h-7 px-2 border border-slate-300 rounded text-[12.5px]"
          />
        ) : (
          <>
            {dateFmt(inst.due_date)}
            {statusKey === "overdue" && overdueDays > 0 && (
              <span className="ml-2 text-rose-600 text-[10.5px] font-semibold">
                {I18n.t("projects.payments.overdue_days", { count: overdueDays })}
              </span>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-2 text-[12.5px] text-slate-900 tabular-nums text-right">
        {editing ? (
          <input
            type      = "text"
            inputMode = "decimal"
            value     = {centsToEuros(draft.amount_cents)}
            onChange  = {e => setDraft({ ...draft, amount_cents: eurosToCents(e.target.value) })}
            className = "h-7 px-2 w-28 text-right border border-slate-300 rounded text-[12.5px]"
          />
        ) : (
          new Intl.NumberFormat("fr-FR", { style: "currency", currency: currency || "EUR" })
            .format(inst.amount_cents / 100)
        )}
      </td>
      <td className="px-3 py-2 text-[12.5px] text-slate-700 tabular-nums text-right">
        {new Intl.NumberFormat("fr-FR", { style: "currency", currency: currency || "EUR" })
          .format(inst.paid_amount_cents / 100)}
      </td>
      <td className="px-3 py-2 text-[12.5px] text-slate-900 font-medium tabular-nums text-right">
        {new Intl.NumberFormat("fr-FR", { style: "currency", currency: currency || "EUR" })
          .format(remaining / 100)}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium ring-1 w-fit ${STATUS_BADGE[statusKey] || STATUS_BADGE.scheduled}`}>
            {I18n.t(`projects.payments.statuses.${statusKey}`)}
          </span>
          {inst.last_reminder_level > 0 && (
            <span
              title={inst.last_reminder_sent_at ? dateFmt(inst.last_reminder_sent_at) : ""}
              className="inline-flex items-center gap-1 text-[10px] text-slate-600 w-fit"
            >
              <FontAwesomeIcon icon="bell" className="text-rose-500" />
              {I18n.t("projects.payments.relance_level_label", { level: inst.last_reminder_level })}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {canManage && (
          <div className="inline-flex items-center gap-1">
            {editing ? (
              <>
                <IconButton size="sm" title={I18n.t("button.save")} onClick={submitEdit}>
                  <FontAwesomeIcon icon="check" />
                </IconButton>
                <IconButton size="sm" title={I18n.t("button.cancel")} onClick={() => setEditing(false)}>
                  <FontAwesomeIcon icon="xmark" />
                </IconButton>
              </>
            ) : (
              <>
                {remaining > 0 && (
                  <IconButton size="sm" title={I18n.t("projects.payments.record_payment")} onClick={() => onPay(inst)}>
                    <FontAwesomeIcon icon="money-bill-wave" />
                  </IconButton>
                )}
                {remaining > 0 && hasBankCandidates && (
                  <IconButton size="sm" title={I18n.t("projects.lettrage.action")} onClick={() => onLettrage(inst)}>
                    <FontAwesomeIcon icon="link" />
                  </IconButton>
                )}
                {statusKey === "overdue" && (
                  <IconButton size="sm" title={I18n.t("projects.payments.send_relance")} onClick={() => onRemind(inst)}>
                    <FontAwesomeIcon icon="bell" />
                  </IconButton>
                )}
                <IconButton size="sm" title={I18n.t("button.edit")} onClick={() => setEditing(true)}>
                  <FontAwesomeIcon icon="pen" />
                </IconButton>
                <IconButton tone="danger" size="sm" title={I18n.t("button.delete")} onClick={() => onDelete(inst)}>
                  <FontAwesomeIcon icon="trash" />
                </IconButton>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

const InvoiceCard = ({ row, canManage, bankTransactions, onUpdate, onConsumeBankTransaction }) => {
  const invoice  = row.invoice
  const currency = row.installments[0]?.currency || "EUR"
  const [lettrageTarget, setLettrageTarget] = useState(null)
  const [showGenerate, setShowGenerate] = useState(false)
  const [genCount, setGenCount]         = useState(1)
  const [genFirstDate, setGenFirstDate] = useState(invoice.expiration_date || todayISO())
  const [genInterval, setGenInterval]   = useState(30)
  const [showAdd, setShowAdd]           = useState(false)
  const [draft, setDraft]               = useState({
    due_date:     invoice.expiration_date || todayISO(),
    amount_cents: 0,
    label:        "",
  })

  const replaceRowInstallments = (next) => onUpdate({ ...row, installments: next })

  const handleCreate = () => {
    if (!draft.amount_cents || draft.amount_cents <= 0) return
    createInstallment(invoice.id, draft).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      replaceRowInstallments([...row.installments, response])
      setShowAdd(false)
      setDraft({ due_date: invoice.expiration_date || todayISO(), amount_cents: 0, label: "" })
    })
  }

  const handleUpdate = (inst, body) => updateInstallment(invoice.id, inst.id, body).then(response => {
    if (response?.errors) { flashAlert(response.errors.join(", ")); return Promise.reject() }
    replaceRowInstallments(row.installments.map(i => i.id === inst.id ? response : i))
  })

  const handleDelete = (inst) => {
    if (!confirm(I18n.t("projects.payments.confirm_destroy"))) return
    destroyInstallment(invoice.id, inst.id).then(response => {
      if (response?.success) replaceRowInstallments(row.installments.filter(i => i.id !== inst.id))
    })
  }

  const handleGenerate = () => {
    generateDefaultInstallments(invoice.id, {
      count:          genCount,
      first_due_date: genFirstDate,
      interval_days:  genInterval,
    }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      replaceRowInstallments(response.installments)
      setShowGenerate(false)
    })
  }

  const handleRemind = (inst) => {
    const nextLevel = inst.next_reminder_level || Math.min((inst.last_reminder_level || 0) + 1, 3)
    if (!confirm(I18n.t("projects.payments.confirm_send_relance", { level: nextLevel }))) return
    sendReminder(invoice.id, inst.id, { level: nextLevel }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      flashNotice(I18n.t("projects.payments.relance_sent", { level: response.level }))
      replaceRowInstallments(row.installments.map(i => i.id === inst.id ? response.installment : i))
    })
  }

  const handleMatch = (bankTransactionId) => {
    matchBankTransaction(invoice.id, lettrageTarget.id, { bank_transaction_id: bankTransactionId }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      replaceRowInstallments(row.installments.map(i => i.id === lettrageTarget.id ? response.installment : i))
      onConsumeBankTransaction(bankTransactionId)
      setLettrageTarget(null)
      flashNotice(I18n.t("projects.lettrage.matched"))
    })
  }

  const handlePay = (inst) => {
    const max     = inst.remaining_cents
    const raw     = prompt(I18n.t("projects.payments.payment_amount_prompt"), centsToEuros(max))
    if (raw == null) return
    const cents   = eurosToCents(raw)
    if (cents <= 0 || cents > max) {
      flashAlert(I18n.t("projects.payments.errors.invalid_amount"))
      return
    }
    recordPayment(invoice.id, inst.id, {
      amount_cents:   cents,
      payment_date:   todayISO(),
      payment_method: "bank_transfer",
    }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      replaceRowInstallments(row.installments.map(i => i.id === inst.id ? response.installment : i))
    })
  }

  const KIND_TONES = {
    down_payment: "bg-blue-50    text-blue-700   ring-blue-200",
    situation:    "bg-slate-100  text-slate-700  ring-slate-200",
    dgd:          "bg-emerald-50 text-emerald-700 ring-emerald-200",
    invoice:      "bg-slate-100  text-slate-700  ring-slate-200",
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <header className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <a href={invoice.path} className="text-[13.5px] font-semibold text-slate-900 hover:text-blue-700 truncate">
              {invoice.number || invoice.title}
            </a>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ring-1 ${KIND_TONES[invoice.kind]}`}>
              {invoice.kind_label}
            </span>
            <span className="text-[11px] text-slate-500">· {invoice.status_label}</span>
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5 inline-flex gap-3">
            <span>{I18n.t("projects.payments.total")} <strong className="text-slate-700">{fmt(invoice.total)}</strong></span>
            <span>{I18n.t("projects.payments.paid")} <strong className="text-emerald-700">{fmt(row.paid)}</strong></span>
            <span>{I18n.t("projects.payments.remaining")} <strong className="text-slate-700">{fmt(row.remaining)}</strong></span>
            {row.overdue_cents > 0 && (
              <span>{I18n.t("projects.payments.overdue")} <strong className="text-rose-700">{fmt(row.overdue)}</strong></span>
            )}
          </div>
        </div>
        {canManage && row.installments.length === 0 && (
          <Button variant="secondary" size="sm" onClick={() => setShowGenerate(v => !v)}>
            <FontAwesomeIcon icon="layer-group" /> {I18n.t("projects.payments.generate_schedule")}
          </Button>
        )}
      </header>

      {showGenerate && (
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
              {I18n.t("projects.payments.installments_count")}
            </span>
            <input type="number" min="1" max="12" value={genCount}
                   onChange={e => setGenCount(parseInt(e.target.value || "1", 10))}
                   className="h-8 w-20 px-2 border border-slate-300 rounded text-[12.5px]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
              {I18n.t("projects.payments.first_due_date")}
            </span>
            <input type="date" value={genFirstDate}
                   onChange={e => setGenFirstDate(e.target.value)}
                   className="h-8 px-2 border border-slate-300 rounded text-[12.5px]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
              {I18n.t("projects.payments.interval_days")}
            </span>
            <input type="number" min="1" value={genInterval}
                   onChange={e => setGenInterval(parseInt(e.target.value || "30", 10))}
                   className="h-8 w-24 px-2 border border-slate-300 rounded text-[12.5px]" />
          </label>
          <Button variant="primary" size="sm" onClick={handleGenerate}>
            <FontAwesomeIcon icon="wand-magic-sparkles" /> {I18n.t("projects.payments.generate")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowGenerate(false)}>
            {I18n.t("button.cancel")}
          </Button>
        </div>
      )}

      {row.installments.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 text-left">
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">{I18n.t("projects.payments.label")}</th>
                <th className="px-3 py-2 font-medium">{I18n.t("projects.payments.due_date")}</th>
                <th className="px-3 py-2 font-medium text-right">{I18n.t("projects.payments.amount")}</th>
                <th className="px-3 py-2 font-medium text-right">{I18n.t("projects.payments.paid")}</th>
                <th className="px-3 py-2 font-medium text-right">{I18n.t("projects.payments.remaining")}</th>
                <th className="px-3 py-2 font-medium">{I18n.t("projects.payments.status")}</th>
                <th className="px-3 py-2 font-medium text-right" />
              </tr>
            </thead>
            <tbody>
              {row.installments.map(inst => (
                <InstallmentRow
                  key                = {inst.id}
                  inst               = {inst}
                  currency           = {currency}
                  canManage          = {canManage}
                  hasBankCandidates  = {(bankTransactions || []).length > 0}
                  onChange           = {handleUpdate}
                  onPay              = {handlePay}
                  onRemind           = {handleRemind}
                  onLettrage         = {(target) => setLettrageTarget(target)}
                  onDelete           = {handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-4 py-6 text-[12.5px] text-slate-500 text-center">
          {I18n.t("projects.payments.empty")}
        </div>
      )}

      {canManage && (
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60">
          {showAdd ? (
            <div className="flex flex-wrap items-end gap-2">
              <input
                type      = "text"
                value     = {draft.label}
                onChange  = {e => setDraft({ ...draft, label: e.target.value })}
                placeholder = {I18n.t("projects.payments.installment_label_placeholder")}
                className = "h-8 flex-1 min-w-32 px-2 border border-slate-300 rounded text-[12.5px]"
              />
              <input
                type      = "date"
                value     = {draft.due_date}
                onChange  = {e => setDraft({ ...draft, due_date: e.target.value })}
                className = "h-8 px-2 border border-slate-300 rounded text-[12.5px]"
              />
              <input
                type      = "text"
                inputMode = "decimal"
                placeholder = "0,00"
                value     = {draft.amount_cents ? centsToEuros(draft.amount_cents) : ""}
                onChange  = {e => setDraft({ ...draft, amount_cents: eurosToCents(e.target.value) })}
                className = "h-8 w-28 px-2 text-right border border-slate-300 rounded text-[12.5px]"
              />
              <Button variant="primary" size="sm" onClick={handleCreate}>
                <FontAwesomeIcon icon="plus" /> {I18n.t("button.add")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>
                {I18n.t("button.cancel")}
              </Button>
            </div>
          ) : (
            <button type="button" onClick={() => setShowAdd(true)}
                    className="text-[12.5px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1.5">
              <FontAwesomeIcon icon="plus" />
              {I18n.t("projects.payments.add_installment")}
            </button>
          )}
        </div>
      )}

      {lettrageTarget && (
        <LettrageModal
          installment  = {lettrageTarget}
          transactions = {bankTransactions || []}
          onClose      = {() => setLettrageTarget(null)}
          onConfirm    = {handleMatch}
        />
      )}
    </div>
  )
}

const PaymentsSection = ({ payments: initial, policies }) => {
  const [rows, setRows]                       = useState(initial?.rows || [])
  const [bankTransactions, setBankTransactions] = useState(initial?.unmatched_bank_transactions || [])
  const canManage = !!policies?.can_manage_payments

  const consumeBankTransaction = (id) => {
    setBankTransactions(prev => prev.filter(t => t.id !== id))
  }

  const totals = useMemo(() => {
    const acc = { total: 0, paid: 0, remaining: 0, overdue: 0 }
    rows.forEach(row => {
      acc.total     += row.invoice.total_cents
      acc.paid      += row.paid_cents
      acc.remaining += row.remaining_cents
      acc.overdue   += row.overdue_cents
    })
    const fmtEuros = c => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(c / 100)
    return {
      total:     { cents: acc.total,     formatted: fmtEuros(acc.total) },
      paid:      { cents: acc.paid,      formatted: fmtEuros(acc.paid) },
      remaining: { cents: acc.remaining, formatted: fmtEuros(acc.remaining) },
      overdue:   { cents: acc.overdue,   formatted: fmtEuros(acc.overdue) },
    }
  }, [rows])

  const recomputeRow = (row) => {
    const paidCents      = row.installments.reduce((acc, i) => acc + (i.paid_amount_cents || 0), 0)
    const scheduledCents = row.installments.reduce((acc, i) => acc + (i.amount_cents      || 0), 0)
    const totalCents     = row.invoice.total_cents
    const overdueCents   = row.installments
      .filter(i => i.status === "overdue")
      .reduce((acc, i) => acc + (i.remaining_cents || 0), 0)
    const fmtEuros = c => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(c / 100)
    const remaining = Math.max(totalCents - paidCents, 0)
    const unscheduled = Math.max(totalCents - scheduledCents, 0)
    return {
      ...row,
      paid_cents:        paidCents,
      paid:              { cents: paidCents,      formatted: fmtEuros(paidCents) },
      remaining_cents:   remaining,
      remaining:         { cents: remaining,      formatted: fmtEuros(remaining) },
      scheduled_cents:   scheduledCents,
      unscheduled_cents: unscheduled,
      unscheduled:       { cents: unscheduled,    formatted: fmtEuros(unscheduled) },
      overdue_cents:     overdueCents,
      overdue:           { cents: overdueCents,   formatted: fmtEuros(overdueCents) },
    }
  }

  const upsertRow = (next) => {
    setRows(prev => prev.map(r => r.invoice.id === next.invoice.id ? recomputeRow(next) : r))
  }

  if (rows.length === 0) {
    return (
      <section id="section-payments" className="px-7 scroll-mt-24">
        <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.payments.title")}</h2>
        <p className="text-[12.5px] text-slate-500 mt-1">{I18n.t("projects.payments.no_invoices")}</p>
      </section>
    )
  }

  return (
    <section id="section-payments" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.payments.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{I18n.t("projects.payments.subtitle")}</p>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatTile label={I18n.t("projects.payments.total")}     value={totals.total.formatted}     />
        <StatTile label={I18n.t("projects.payments.paid")}      value={totals.paid.formatted}      tone="bg-emerald-50 border-emerald-200" />
        <StatTile label={I18n.t("projects.payments.remaining")} value={totals.remaining.formatted} />
        <StatTile label={I18n.t("projects.payments.overdue")}   value={totals.overdue.formatted}   tone={totals.overdue.cents > 0 ? "bg-rose-50 border-rose-200" : ""} />
      </div>

      <div className="space-y-3">
        {rows.map(row => (
          <InvoiceCard
            key                       = {row.invoice.id}
            row                       = {row}
            canManage                 = {canManage}
            bankTransactions          = {bankTransactions}
            onUpdate                  = {upsertRow}
            onConsumeBankTransaction  = {consumeBankTransaction}
          />
        ))}
      </div>
    </section>
  )
}

export default PaymentsSection
